import { useEffect, useRef, useState, useCallback } from "react";
import { loadGoogleMaps } from "../services/loadGoogleMaps";

const AddressAutocompletePlaces = ({ value, onSelect, placeholder }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);
  const onSelectRef = useRef(onSelect);

  const [apiError, setApiError] = useState(false);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  const getPostalCode = (place) => {
    const component = place.address_components?.find((c) =>
      c.types.includes("postal_code")
    );
    return component ? component.long_name : null;
  };

  const clearAutocompleteListener = () => {
    if (listenerRef.current) {
      listenerRef.current.remove();
      listenerRef.current = null;
    }
  };

  const initAutocomplete = useCallback(async () => {
    if (!inputRef.current) return;

    try {
      setApiError(false);

      const google = await loadGoogleMaps(
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      );

      if (!google?.maps?.places || !inputRef.current) {
        setApiError(true);
        return;
      }

      clearAutocompleteListener();

      autocompleteRef.current = new google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: "ca" },
          fields: [
            "formatted_address",
            "geometry",
            "address_components",
            "place_id",
            "name",
          ],
        }
      );

      listenerRef.current = autocompleteRef.current.addListener(
        "place_changed",
        () => {
          const place = autocompleteRef.current?.getPlace();

          if (!place?.geometry) return;

          const postalCode = getPostalCode(place);

          const fullAddress =
            place.formatted_address ||
            inputRef.current?.value ||
            "";

          onSelectRef.current({
            address: fullAddress,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            postal_code: postalCode,
            place_id: place.place_id || null,
            place_name: place.name || null,
          });
        }
      );
    } catch (error) {
      console.error("Erreur Google Maps Places:", error);
      setApiError(true);
    }
  }, []);

  useEffect(() => {
    initAutocomplete();

    return () => {
      clearAutocompleteListener();
    };
  }, [initAutocomplete]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        initAutocomplete();
      }
    };

    window.addEventListener("focus", initAutocomplete);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", initAutocomplete);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [initAutocomplete]);

  return (
    <div className="address-autocomplete-wrapper">
      <input
        ref={inputRef}
        type="text"
        value={value || ""}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          onSelect({
            address: e.target.value,
            lat: null,
            lng: null,
            postal_code: null,
            place_id: null,
            place_name: null,
          });
        }}
      />

      {apiError && (
        <div style={{ marginTop: "6px" }}>
          <small>Suggestions d’adresse indisponibles pour le moment.</small>
          <button
            type="button"
            onClick={initAutocomplete}
            style={{ marginLeft: "8px" }}
          >
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
};

export default AddressAutocompletePlaces;